import { api } from "./api";
import { getMarketingUrl } from "./marketing-url";
import { REFERRAL_ATTRIBUTION_QUERY_KEY } from "./onboarding";

export type ReferralLinkKind = "buyer_brag" | "consumer_referral";

export interface ReferralLink {
  token: string;
  kind: ReferralLinkKind | string;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

/**
 * Reward lifecycle from `reward_ledger`: `earned` (owed, not yet fulfilled),
 * `fulfilled` (paid out / applied), `reversed` (revoked after being earned —
 * never deleted, so the referrer's history stays honest). `kind` includes
 * monetary rewards (`consumer_gift_card`, `consumer_cash`). A monetary row can
 * only exist if the admin reward config was enabled at earn time, so the row's
 * presence IS the authorization — an empty `rewards` array must never be read
 * as "zero reward," only as "nothing to show yet."
 */
export type ReferralRewardStatus = "earned" | "fulfilled" | "reversed";

export interface ReferralReward {
  id: string;
  kind: string;
  status: ReferralRewardStatus | string;
  /**
   * What THIS reward promised, frozen on the ledger row at earn time — never
   * the live admin config, so a later config change cannot rewrite what the
   * referrer was told. `null` means no promise was recorded (legacy rows,
   * `pro_crm_month`); it must render differently from an explicit `0`.
   */
  promised_value_cents: number | null;
  fulfilled_at: string | null;
  reversed_at: string | null;
  created_at: string;
}

/**
 * The LIVE admin-set monetary incentive (referral_reward_config, D11), as
 * projected by GET /api/referrals/me. `kind`/`value_cents`/`milestone` are
 * only ever non-null together with `enabled:true`; a disabled config arrives
 * as an explicit all-null block. `version` is an opaque identity for the
 * enabled row — the dismissal key that lets "a new enabled incentive
 * re-surfaces the banner once" be expressed; it is never displayed.
 */
export type ReferralIncentiveKind = "cash" | "gift_card";
export type ReferralEarnMilestone = "publish" | "connection";

export interface ReferralIncentiveWire {
  enabled: boolean;
  kind: ReferralIncentiveKind | string | null;
  value_cents: number | null;
  milestone: ReferralEarnMilestone | string | null;
  version: string | null;
}

/** A validated, ENABLED incentive — the only shape a surface may state an amount from. */
export interface ReferralIncentive {
  enabled: true;
  kind: ReferralIncentiveKind;
  value_cents: number;
  milestone: ReferralEarnMilestone;
  version: string;
}

function isIncentiveKind(value: unknown): value is ReferralIncentiveKind {
  return value === "cash" || value === "gift_card";
}

function isEarnMilestone(value: unknown): value is ReferralEarnMilestone {
  return value === "publish" || value === "connection";
}

/**
 * Narrows the wire block to a promise the client can render. Anything short
 * of a fully-populated, recognised, enabled block resolves to null — "nothing
 * to promise" — so an unknown kind or a half-null row can never become
 * fabricated copy. A pre-block backend (field absent) is null too.
 */
export function normalizeReferralIncentive(
  raw: ReferralIncentiveWire | null | undefined,
): ReferralIncentive | null {
  if (!raw || raw.enabled !== true) return null;
  if (!isIncentiveKind(raw.kind) || !isEarnMilestone(raw.milestone)) {
    return null;
  }
  if (typeof raw.value_cents !== "number" || !Number.isFinite(raw.value_cents)) {
    return null;
  }
  if (typeof raw.version !== "string" || raw.version === "") return null;
  return {
    enabled: true,
    kind: raw.kind,
    value_cents: raw.value_cents,
    milestone: raw.milestone,
    version: raw.version,
  };
}

export interface MyReferralsResponse {
  links: ReferralLink[];
  rewards: ReferralReward[];
  /** 1-based rank, `null` until the area's rank is revealed (cold-start). */
  waitlist_position: number | null;
  /** Friends attributed through the caller's `consumer_referral` links ONLY. */
  signup_count: number;
  /**
   * The always-on reward's status: how many referred friends' publishes
   * actually advanced the caller's geo-waitlist position.
   */
  waitlist_credits: number;
  /** Live monetary incentive; optional so a pre-block backend still parses. */
  incentive?: ReferralIncentiveWire | null;
}

/** The real, backend-driven referral summary for the consumer settings card. */
export interface ConsumerReferralSummary {
  shareUrl: string;
  signupCount: number;
  rewards: ReferralReward[];
  waitlistPosition: number | null;
  waitlistCredits: number;
  /** Validated enabled incentive, or null when there is no monetary promise to state. */
  incentive: ReferralIncentive | null;
}

function isUsable(link: ReferralLink, now = Date.now()) {
  return (
    !link.revoked_at &&
    (!link.expires_at || new Date(link.expires_at).getTime() > now)
  );
}

/**
 * Reuses the caller's newest active link of `kind` before minting another
 * one. Shared by every referral-link surface so "reuse before mint" logic
 * lives in exactly one place. `mine` is the caller's already-fetched
 * `/api/referrals/me` read (the caller decides whether it also needs other
 * fields off that same response, e.g. signup_count/rewards).
 */
async function reuseOrMintLink(
  mine: MyReferralsResponse,
  kind: ReferralLinkKind,
  token: string | undefined,
): Promise<ReferralLink> {
  const existing = mine.links
    .filter((link) => link.kind === kind && isUsable(link))
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];

  return (
    existing ??
    (await api.post<ReferralLink>("/api/referrals/links", { kind }, token))
  );
}

/**
 * Reuses the caller's active buyer_brag C1 link before minting another one.
 * The token is deliberately opaque; the referral rail remains the token and
 * attribution authority, while this client only turns it into a share URL.
 */
export async function getOrCreateBuyerBragLink(
  getToken: () => Promise<string | null>,
): Promise<string> {
  const token = (await getToken()) ?? undefined;
  const mine = await api.get<MyReferralsResponse>("/api/referrals/me", token);
  const link = await reuseOrMintLink(mine, "buyer_brag", token);

  // This short route intentionally does not overload legacy agent-invite
  // `ref`; Marketing forwards it as `referral_token` after the click.
  return getMarketingUrl(`/r/${encodeURIComponent(link.token)}`);
}

/**
 * Fetches the caller's full referral summary: the share link from the server's
 * reuse-or-mint endpoint (`POST /api/referrals/consumer-link`) and the tracking
 * data from `GET /api/referrals/me`, issued concurrently.
 *
 * The share link carries the real, attribution-consuming `?referral_token=`
 * query param (`REFERRAL_ATTRIBUTION_QUERY_KEY`) — never the retired,
 * dead `?referral_code=` client-side vanity slug.
 */
export async function getConsumerReferralSummary(
  getToken: () => Promise<string | null>,
  options?: { path?: string },
): Promise<ConsumerReferralSummary> {
  const token = (await getToken()) ?? undefined;
  // The share link comes from the SERVER's reuse-or-mint endpoint, not from
  // client-side filtering of /me. The backend owns what "active" means
  // (revocation + expiry rules), so resolving it here would be a second
  // implementation of that policy in a second language, free to drift.
  // /me is still read for the tracking section (signup count, rewards).
  const [mine, link] = await Promise.all([
    api.get<MyReferralsResponse>("/api/referrals/me", token),
    api.post<ReferralLink>("/api/referrals/consumer-link", undefined, token),
  ]);
  const path = options?.path ?? "/";

  return {
    shareUrl: getMarketingUrl(path, {
      [REFERRAL_ATTRIBUTION_QUERY_KEY]: link.token,
    }),
    signupCount: mine.signup_count ?? 0,
    rewards: mine.rewards ?? [],
    waitlistPosition: mine.waitlist_position ?? null,
    waitlistCredits: mine.waitlist_credits ?? 0,
    incentive: normalizeReferralIncentive(mine.incentive),
  };
}

/**
 * Returns just the caller's consumer-referral share URL. Prefer
 * `getConsumerReferralSummary` when the caller also needs signup/reward data —
 * it resolves both in one concurrent pass.
 */
export async function getOrCreateConsumerReferralLink(
  getToken: () => Promise<string | null>,
  options?: { path?: string },
): Promise<string> {
  const summary = await getConsumerReferralSummary(getToken, options);
  return summary.shareUrl;
}
